//ts-check
import {move} from '@dnd-kit/helpers'

export const MAX_BRANDS_TO_DISPLAY = 6

/**
 * Sort catalog items by ascending display order, pushing items without a valid order to the end.
 *
 * @template {{catalogDisplayOrder?: number}} T
 * @param {T[]} [items=[]]
 * @returns {T[]}
 */
export const sortByDisplayOrder = items => {
  return [...items].sort((left, right) => {
    const leftOrder = Number.isFinite(left?.catalogDisplayOrder) ? left.catalogDisplayOrder : Number.MAX_SAFE_INTEGER
    const rightOrder = Number.isFinite(right?.catalogDisplayOrder) ? right.catalogDisplayOrder : Number.MAX_SAFE_INTEGER

    return leftOrder - rightOrder
  })
}

/**
 * Sort brands, categories, and subcategories into the initial UI order.
 *
 * @param {import('@/types/productCatalogTypes').ProductCategoriesData} catalogData
 * @returns {import('@/types/productCatalogTypes').ProductCategoriesData}
 */
export const sortCatalogData = catalogData => ({
  brands: sortByDisplayOrder(catalogData?.brands ?? []),
  categories: sortByDisplayOrder(catalogData?.categories ?? []).map(category => ({
    ...category,
    subCategories: sortByDisplayOrder(category?.subCategories ?? []),
  })),
})

/**
 * Rewrite catalogDisplayOrder values to match the current array order.
 *
 * @template {{catalogDisplayOrder?: number}} T
 * @param {T[]} [items=[]]
 * @returns {(T & {catalogDisplayOrder: number})[]}
 */
export const updateDisplayOrder = items => {
  return (items ?? []).map((item, index) => ({
    ...item,
    catalogDisplayOrder: index + 1,
  }))
}

/**
 * Reorder catalog items after a drag event and persist the new display order values.
 *
 * @template {{catalogDisplayOrder?: number}} T
 * @param {T[]} [items=[]]
 * @param {import('@dnd-kit/dom').DragEndEvent} event
 * @returns {(T & {catalogDisplayOrder: number})[]}
 */
export const reorderCatalogItems = (items, event) => {
  return updateDisplayOrder(move(items ?? [], event))
}

/**
 * Reorder subcategories for a single category and update their catalogDisplayOrder values.
 *
 * @param {import('@/types/productCatalogTypes').ProductCategory[]} [categories=[]]
 * @param {string} categoryId
 * @param {import('@dnd-kit/dom').DragEndEvent} event
 * @returns {import('@/types/productCatalogTypes').ProductCategory[]}
 */
export const reorderCategorySubcategories = (categories, categoryId, event) => {
  return (categories ?? []).map(category => {
    if (category.id !== categoryId) {
      return category
    }

    return {
      ...category,
      subCategories: reorderCatalogItems(category.subCategories ?? [], event),
    }
  })
}

/**
 * Resolve the preferred display name for a catalog item.
 *
 * @param {{id?: string, name?: import('@/types/productCatalogTypes').ProductCategoryLocalizedName}} item
 * @returns {string}
 */
export const getCatalogItemName = item => {
  return item?.name?.en ?? item?.name?.es_MX ?? item?.id ?? ''
}

/**
 * Count hidden subcategories within a category.
 *
 * @param {import('@/types/productCatalogTypes').ProductSubcategory[]} [subcategories=[]]
 * @returns {number}
 */
export const getHiddenSubcategoriesCount = subcategories => {
  return (subcategories ?? []).filter(subcategory => !subcategory.visible).length
}

/**
 * Format a numeric position using an ordinal suffix.
 *
 * @param {number} position
 * @returns {string}
 */
export const getPositionLabel = position => {
  const remainderTen = position % 10
  const remainderHundred = position % 100

  if (remainderTen === 1 && remainderHundred !== 11) {
    return `${position}st Position`
  }

  if (remainderTen === 2 && remainderHundred !== 12) {
    return `${position}nd Position`
  }

  if (remainderTen === 3 && remainderHundred !== 13) {
    return `${position}rd Position`
  }

  return `${position}th Position`
}

/**
 * Convert catalog UI state into the backend publish payload.
 *
 * @param {{
 *   brands?: import('@/types/productCatalogTypes').ProductCategoryBrand[],
 *   categories?: import('@/types/productCatalogTypes').ProductCategory[],
 * }} catalogData
 * @returns {{
 *   items: Array<
 *     | {id: string, type: 'displaybrand', catalogDisplayOrder: number}
 *     | {
 *         id: string,
 *         type: 'displaycategory',
 *         catalogDisplayOrder: number,
 *         items: Array<{id: string, catalogDisplayOrder: number, visible: boolean}>
 *       }
 *   >
 * }}
 */
export const transformCatalogToPublishPayload = catalogData => {
  const brandItems = (catalogData?.brands ?? []).map((brand, index) => ({
    id: brand.id,
    type: 'displaybrand',
    catalogDisplayOrder: brand.catalogDisplayOrder ?? index + 1,
  }))

  const categoryItems = (catalogData?.categories ?? []).map((category, index) => ({
    id: category.id,
    type: 'displaycategory',
    catalogDisplayOrder: category.catalogDisplayOrder ?? index + 1,
    items: (category.subCategories ?? []).map((subcategory, subIndex) => ({
      id: subcategory.id,
      catalogDisplayOrder: subcategory.catalogDisplayOrder ?? subIndex + 1,
      visible: Boolean(subcategory.visible),
    })),
  }))

  return {
    items: [...brandItems, ...categoryItems],
  }
}
